import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { motion, useAnimationControls } from 'framer-motion'
import React, { createContext, useContext } from 'react'
import styled, { css } from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import type { DebouncedFunc } from 'utils/lodashTypes'

export const MenuStyles = css`
  overflow: hidden;
  border-radius: 0.25rem;
  text-align: left;
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
  background-color: ${({ theme }) => theme.forecast.background2};
`

export const FilteredSearchContainer = styled.div`
  ${MenuStyles}
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const StyledDropdownContent = styled(DropdownMenu.Content)`
  ${MenuStyles}
`

const StyledMotionDiv = styled(motion.div)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const DropdownSearchMenuContext = createContext<{
  closeMenu(): Promise<void>
  firstItemRef: React.MutableRefObject<HTMLDivElement | null>
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  searchText: string
  debouncedResults?: DebouncedFunc<
    (e: React.ChangeEvent<HTMLInputElement>) => void
  >
}>({
  firstItemRef: { current: null },
  inputRef: { current: null },
  searchText: '',
  closeMenu: () => Promise.resolve(),
  debouncedResults: undefined,
})

export const StyledDropdownItem = styled(DropdownMenu.Item)`
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.activeText};
  user-select: none;
  border: 1px solid transparent;
  width: 100%;

  &[data-highlighted] {
    color: ${({ theme }) => theme.forecast.activeText};
    border-color: ${({ theme }) => theme.forecast.background4};
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  & [data-edit-link='true'] {
    display: none;
  }

  &:hover [data-edit-link='true'] {
    cursor: pointer;
    display: block;
  }
`

export function DropdownSearchMenuItem({
  isFirstItem,
  children,
  onSelect = () => {
    // noop
  },
}: {
  isFirstItem?: boolean
  children: React.ReactNode
  onSelect?: () => void
}) {
  const controls = useAnimationControls()
  const { closeMenu, firstItemRef } = useContext(DropdownSearchMenuContext)
  const { theme } = useGeckoTheme()

  return (
    <StyledDropdownItem
      ref={isFirstItem ? firstItemRef : undefined}
      onSelect={async (e) => {
        e.preventDefault()

        await controls.start({
          backgroundColor: theme.forecast.secondaryText,
          color: theme.forecast.background1,
          transition: { duration: 0.04 },
        })
        await controls.start({
          backgroundColor: theme.forecast.background2,
          color: theme.forecast.activeText,
          transition: { duration: 0.04 },
        })

        await closeMenu()
        onSelect()
      }}
      asChild>
      <StyledMotionDiv animate={controls}>{children}</StyledMotionDiv>
    </StyledDropdownItem>
  )
}
